/**
 * MobileBautagesbericht V3 – Premium, VOB-konform, Mobile First
 * Jede Aufgabe = eine Karte mit KI-Text, Fotos, Checkliste
 * Stoerungen als Pflichtfeld, Fremdfirmen, Leistungsstand, Unterschrift
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch, useAuth } from "../../App";
import { offlineAuthFetch } from "../../lib/offlineAuthFetch";
import { toast } from "sonner";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import {
  FileText, Sparkles, Save, Download, ChevronLeft, Eye,
  Users, Clock, CloudSun, Edit3, Loader2,
  Camera, AlertTriangle, Calendar, X, Timer,
  MapPin, Shield, History, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle, Info, Building2,
  TrendingUp, Plus, Trash2, Pen, Check, Mic, MicOff,
} from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const API = "/api";

/* ── Fullscreen Photo Overlay ── */
function PhotoOverlay({ src, onClose }) {
  if (!src) return null;
  return (
    <div className="fixed inset-0 z-[999] bg-black/95 flex items-center justify-center" onClick={onClose} data-testid="photo-overlay">
      <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20 z-10">
        <X className="w-6 h-6" />
      </button>
      <img src={src} alt="Originalbild" className="max-w-full max-h-full object-contain" onClick={e => e.stopPropagation()} />
    </div>
  );
}

/* ── Section Wrapper ── */
function Section({ icon: Icon, title, error, children, collapsible, defaultOpen = true, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rounded-2xl border ${error ? "border-red-500/30 bg-red-500/5" : "border-white/8 bg-white/[0.03]"}`} data-testid={testId}>
      <button
        onClick={collapsible ? () => setOpen(v => !v) : undefined}
        className={`w-full flex items-center justify-between px-4 py-3 ${collapsible ? "cursor-pointer" : "cursor-default"}`}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${error ? "text-red-400" : "text-gray-500"}`} />}
          <span className={`text-xs font-semibold tracking-wide uppercase ${error ? "text-red-400" : "text-gray-400"}`}>{title}</span>
        </div>
        {collapsible && (open ? <ChevronUp className="w-3.5 h-3.5 text-gray-600" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-600" />)}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

/* ── Status Badge ── */
const STATUS_COLORS = {
  offen: "bg-gray-500/20 text-gray-400",
  in_arbeit: "bg-amber-500/20 text-amber-400",
  erledigt: "bg-green-500/20 text-green-400",
  blockiert: "bg-red-500/20 text-red-400",
};
const STATUS_LABELS = { offen: "Offen", in_arbeit: "In Arbeit", erledigt: "Erledigt", blockiert: "Blockiert" };

/* ── Activity Card (Premium Task Block) ── */
function ActivityCard({ activity, index, editMode, onChange, onPhotoClick }) {
  const status = activity.source_task_status || "offen";
  const clTotal = activity.checklist_total || 0;
  const clDone = activity.checklist_done || 0;
  const photos = activity.photo_urls || [];
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) { toast.error("Aufnahme zu kurz"); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const res = await authFetch(`${API}/voice/transcribe`, { method: "POST", body: fd, headers: {} });
          if (res.ok) {
            const data = await res.json();
            if (data.text) {
              const prev = activity.description || "";
              onChange(index, "description", prev ? `${prev}\n${data.text}` : data.text);
              toast.success("Sprache erkannt");
            } else { toast.info("Keine Sprache erkannt"); }
          } else { toast.error("Transkription fehlgeschlagen"); }
        } catch { toast.error("Fehler bei Spracherkennung"); }
        setTranscribing(false);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch { toast.error("Mikrofon nicht verfuegbar"); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] overflow-hidden" data-testid={`btb-activity-${index}`}>
      {/* Card Header */}
      <div className="px-4 pt-3 pb-2 border-b border-white/5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-mono text-gray-600">{activity.source_task_number || `#${index + 1}`}</span>
            <h4 className="text-sm font-semibold text-gray-200 mt-0.5 leading-snug">
              {activity.original_task_title || activity.source_task_title || "Taetigkeit"}
            </h4>
          </div>
          <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[status] || STATUS_COLORS.offen}`}>
            {STATUS_LABELS[status] || status}
          </span>
        </div>
        {activity.employee_name && (
          <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
            <Users className="w-3 h-3" />{activity.employee_name}
          </p>
        )}
      </div>

      {/* KI-Text / Description */}
      <div className="px-4 py-3">
        {editMode ? (
          <div className="space-y-2">
            <Textarea
              value={activity.description || ""}
              onChange={e => onChange(index, "description", e.target.value)}
              rows={4}
              className="bg-white/8 border-white/10 text-white text-sm placeholder:text-gray-600 leading-relaxed"
              placeholder="Taetigkeitsbeschreibung..."
              data-testid={`btb-activity-edit-${index}`}
            />
            {/* Voice Input Button */}
            <div className="flex items-center gap-2">
              <button
                onClick={recording ? stopRecording : startRecording}
                disabled={transcribing}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  recording ? "bg-red-500 text-white animate-pulse" :
                  transcribing ? "bg-gray-700 text-gray-400" :
                  "bg-white/8 text-gray-400 hover:bg-white/12 hover:text-white active:bg-white/15"
                }`}
                data-testid={`btb-voice-btn-${index}`}
              >
                {transcribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : recording ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                {transcribing ? "Wird erkannt..." : recording ? "Stoppen" : "Spracheingabe"}
              </button>
              {recording && <span className="text-[10px] text-red-400 animate-pulse">Aufnahme laeuft...</span>}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">{activity.description}</p>
        )}
        {activity.description !== activity.original_task_title && !editMode && (
          <span className="inline-flex items-center gap-1 mt-2 text-[10px] px-2 py-0.5 bg-blue-500/10 text-blue-400 rounded-full">
            <Sparkles className="w-3 h-3" />KI-formuliert
          </span>
        )}
      </div>

      {/* Checklist Progress */}
      {clTotal > 0 && (
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${(clDone / clTotal) * 100}%` }} />
            </div>
            <span className="text-[10px] text-gray-500 font-medium">{clDone}/{clTotal}</span>
          </div>
        </div>
      )}

      {/* Photos */}
      {photos.length > 0 && (
        <div className="px-4 pb-3">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {photos.map((url, pi) => (
              <img
                key={pi}
                src={url}
                alt=""
                className="w-20 h-20 rounded-lg object-cover flex-shrink-0 border border-white/10 cursor-pointer hover:border-blue-400/50 transition-colors"
                onClick={() => onPhotoClick(url)}
                data-testid={`btb-photo-${index}-${pi}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Version History ── */
function VersionHistory({ versions, onClose }) {
  if (!versions) return null;
  return (
    <div className="fixed inset-0 z-[800] bg-black/70 flex items-end justify-center" onClick={onClose} data-testid="btb-version-overlay">
      <div className="relative w-full max-w-lg bg-[#1c1f26] rounded-t-2xl border-t border-white/10 max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#1c1f26] px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-200 flex items-center gap-2"><History className="w-4 h-4" />Versionshistorie</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-gray-400"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-4 py-3 space-y-3">
          {(versions.versions || []).length === 0 ? (
            <p className="text-xs text-gray-600 text-center py-4">Keine Aenderungen bisher</p>
          ) : (
            [...(versions.versions || [])].reverse().map((v, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3" data-testid={`btb-version-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-blue-400">Version {v.version}</span>
                  <span className="text-[10px] text-gray-500">{v.timestamp ? format(new Date(v.timestamp), "dd.MM.yyyy HH:mm", { locale: de }) : ""}</span>
                </div>
                <p className="text-[11px] text-gray-400 mb-1.5">von {v.edited_by_name}</p>
                {Object.entries(v.changes || {}).map(([key, val]) => (
                  <div key={key} className="text-xs mt-1">
                    <span className="text-gray-500 font-medium">{key}:</span>
                    {typeof val === "object" && val.old !== undefined ? (
                      <span className="ml-1 text-gray-400">{String(val.old).slice(0, 40)} &rarr; {String(val.new).slice(0, 40)}</span>
                    ) : (
                      <span className="text-gray-400 ml-1">{JSON.stringify(val)}</span>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════ MAIN COMPONENT ═══════════════ */
export default function MobileBautagesbericht({ projectId, projectName, onBack }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [editMode, setEditMode] = useState(false);
  const [view, setView] = useState("list");
  const [overlayPhoto, setOverlayPhoto] = useState(null);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState(null);

  // Edit state
  const [editActivities, setEditActivities] = useState([]);
  const [editNotes, setEditNotes] = useState("");
  const [editWeather, setEditWeather] = useState("");
  const [editDisruptions, setEditDisruptions] = useState("");
  const [editSubcontractors, setEditSubcontractors] = useState([]);
  const [editWorkStartManual, setEditWorkStartManual] = useState("");
  const [editWorkEndManual, setEditWorkEndManual] = useState("");
  const [editTotalHoursManual, setEditTotalHoursManual] = useState("");
  const [editProgress, setEditProgress] = useState("");

  // Voice recording for disruptions
  const [disruptionRecording, setDisruptionRecording] = useState(false);
  const [disruptionTranscribing, setDisruptionTranscribing] = useState(false);
  const disruptionMRRef = useRef(null);
  const disruptionChunksRef = useRef([]);

  const startDisruptionVoice = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      disruptionChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) disruptionChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(disruptionChunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) { toast.error("Aufnahme zu kurz"); return; }
        setDisruptionTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const res = await authFetch(`${API}/voice/transcribe`, { method: "POST", body: fd, headers: {} });
          if (res.ok) {
            const data = await res.json();
            if (data.text) {
              setEditDisruptions(prev => prev ? `${prev}\n${data.text}` : data.text);
              toast.success("Sprache erkannt");
            } else { toast.info("Keine Sprache erkannt"); }
          } else { toast.error("Transkription fehlgeschlagen"); }
        } catch { toast.error("Fehler bei Spracherkennung"); }
        setDisruptionTranscribing(false);
      };
      mr.start();
      disruptionMRRef.current = mr;
      setDisruptionRecording(true);
    } catch { toast.error("Mikrofon nicht verfuegbar"); }
  };

  const stopDisruptionVoice = () => {
    if (disruptionMRRef.current?.state === "recording") {
      disruptionMRRef.current.stop();
      setDisruptionRecording(false);
    }
  };

  const loadReports = useCallback(async () => {
    setLoading(true);
    const res = await offlineAuthFetch(`${API}/projects/${projectId}/daily-reports`, {}, { cacheKey: `daily-reports-${projectId}` });
    if (res.ok) setReports(await res.json() || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const res = await authFetch(`${API}/projects/${projectId}/daily-reports/generate`, {
        method: "POST",
        body: JSON.stringify({ date: selectedDate, weather: editWeather }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || "Bericht generiert");
        setReport(data.report);
        setView("detail");
        setEditMode(false);
        setValidation(null);
        await loadReports();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler bei der Generierung");
      }
    } catch { toast.error("Fehler bei der Generierung"); }
    setGenerating(false);
  };

  const openReport = (r) => { setReport(r); setView("detail"); setEditMode(false); setValidation(null); };

  const startEdit = () => {
    setEditActivities((report.activities || []).map(a => ({ ...a })));
    setEditNotes(report.notes || "");
    setEditWeather(report.weather || "");
    setEditDisruptions(report.disruptions || "");
    setEditSubcontractors((report.subcontractors || []).map(s => ({ ...s })));
    setEditWorkStartManual(report.work_start_manual || "");
    setEditWorkEndManual(report.work_end_manual || "");
    setEditTotalHoursManual(report.total_work_hours_manual != null ? String(report.total_work_hours_manual) : "");
    setEditProgress(report.progress_percent != null ? String(report.progress_percent) : "");
    setEditMode(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    const body = {
      notes: editNotes,
      weather: editWeather,
      disruptions: editDisruptions,
      activities: editActivities.map(a => ({
        description: a.description, hours: a.hours || 0,
        source_task_id: a.source_task_id, source_task_title: a.source_task_title,
        employee_id: a.employee_id, employee_name: a.employee_name,
      })),
      subcontractors: editSubcontractors,
    };
    if (editWorkStartManual) body.work_start_manual = editWorkStartManual;
    if (editWorkEndManual) body.work_end_manual = editWorkEndManual;
    if (editTotalHoursManual !== "") body.total_work_hours_manual = parseFloat(editTotalHoursManual) || 0;
    if (editProgress !== "") body.progress_percent = parseInt(editProgress) || 0;

    const res = await authFetch(`${API}/projects/${projectId}/daily-reports/${report.id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const updated = await res.json();
      setReport(updated);
      setEditMode(false);
      setValidation(null);
      toast.success("Bericht gespeichert");
      await loadReports();
    } else toast.error("Fehler beim Speichern");
    setSaving(false);
  };

  const validateReport = async () => {
    setValidating(true);
    try {
      const res = await authFetch(`${API}/projects/${projectId}/daily-reports/${report.id}/validate`);
      if (res.ok) {
        const data = await res.json();
        setValidation(data);
        if (!data.valid) toast.error("Bautagesbericht unvollstaendig");
        else toast.success("Alle Pflichtfelder vollstaendig");
      }
    } catch { toast.error("Validierung fehlgeschlagen"); }
    setValidating(false);
  };

  const loadVersions = async () => {
    try {
      const res = await authFetch(`${API}/projects/${projectId}/daily-reports/${report.id}/versions`);
      if (res.ok) { setVersions(await res.json()); setShowVersions(true); }
    } catch { toast.error("Versionen konnten nicht geladen werden"); }
  };

  const downloadPdf = async () => {
    setValidating(true);
    try {
      const vRes = await authFetch(`${API}/projects/${projectId}/daily-reports/${report.id}/validate`);
      if (vRes.ok) {
        const vData = await vRes.json();
        setValidation(vData);
        if (!vData.can_export) { toast.error("Export blockiert: Pflichtfelder unvollstaendig"); setValidating(false); return; }
      }
    } catch { /* proceed */ }
    setValidating(false);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/projects/${projectId}/daily-reports/${report.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `Bautagesbericht_${projectName}_${report.date}.pdf`; a.click();
        URL.revokeObjectURL(url);
        toast.success("PDF heruntergeladen");
      } else toast.error("PDF-Export fehlgeschlagen");
    } catch { toast.error("PDF-Export fehlgeschlagen"); }
  };

  const handleActivityChange = (idx, field, value) => {
    setEditActivities(prev => {
      const u = [...prev];
      u[idx] = { ...u[idx], [field]: value };
      return u;
    });
  };

  const addSubcontractor = () => setEditSubcontractors(prev => [...prev, { company: "", trade: "", workers: 0, notes: "" }]);
  const removeSubcontractor = (idx) => setEditSubcontractors(prev => prev.filter((_, i) => i !== idx));
  const updateSubcontractor = (idx, field, val) => {
    setEditSubcontractors(prev => {
      const u = [...prev];
      u[idx] = { ...u[idx], [field]: val };
      return u;
    });
  };

  const fieldHasError = (field) => validation?.errors?.some(e => e.field === field);

  // ━━━━━━━━━ DETAIL VIEW ━━━━━━━━━
  if (view === "detail" && report) {
    const empDetails = report.employee_details || [];
    const timeline = report.timeline || [];
    const address = report.project_address || "";
    const displayAddress = typeof address === "object"
      ? `${address.street || ""} ${address.house_number || ""}, ${address.zip || ""} ${address.city || ""}`.trim()
      : address;

    const workStart = report.work_start_manual || report.work_start || "";
    const workEnd = report.work_end_manual || report.work_end || "";
    const totalHours = report.total_work_hours_manual != null ? report.total_work_hours_manual : report.total_work_hours || 0;
    const hasManualOverride = !!(report.work_start_manual || report.work_end_manual || report.total_work_hours_manual != null);
    const progressPct = report.progress_percent || 0;
    const disruptions = report.disruptions || "";
    const subs = report.subcontractors || [];

    return (
      <div className="bg-[#0d0e14] text-white min-h-full" data-testid="btb-detail-view">
        {overlayPhoto && <PhotoOverlay src={overlayPhoto} onClose={() => setOverlayPhoto(null)} />}
        {showVersions && <VersionHistory versions={versions} onClose={() => setShowVersions(false)} />}

        {/* ── Sticky Header ── */}
        <div className="sticky top-0 z-10 bg-[#0d0e14]/95 backdrop-blur-lg border-b border-white/8 px-4 py-3">
          <div className="flex items-center justify-between">
            <button onClick={() => setView("list")} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors" data-testid="btb-back-btn">
              <ChevronLeft className="w-4 h-4" />Zurueck
            </button>
            <div className="flex gap-1.5">
              {!editMode ? (
                <>
                  <button onClick={loadVersions} className="p-2 bg-white/8 rounded-lg text-gray-400 hover:text-white transition-colors" data-testid="btb-versions-btn" title="Versionen">
                    <History className="w-4 h-4" />
                  </button>
                  <button onClick={startEdit} className="flex items-center gap-1 text-xs px-3 py-2 bg-white/8 rounded-lg text-blue-400 hover:bg-white/12 transition-colors font-medium" data-testid="btb-edit-btn">
                    <Edit3 className="w-3.5 h-3.5" />Bearbeiten
                  </button>
                </>
              ) : (
                <button onClick={saveEdit} disabled={saving} className="flex items-center gap-1 text-xs px-3 py-2 bg-green-600 rounded-lg text-white font-semibold" data-testid="btb-save-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Speichern
                </button>
              )}
              <button onClick={downloadPdf} disabled={validating} className="flex items-center gap-1 text-xs px-3 py-2 bg-blue-600 rounded-lg text-white font-semibold" data-testid="btb-pdf-btn">
                {validating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}PDF
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 py-5 space-y-4 pb-20">

          {/* ═══ 1. KOPFBEREICH ═══ */}
          <div className="relative overflow-hidden rounded-2xl border border-white/8 bg-gradient-to-br from-blue-600/8 via-indigo-600/5 to-transparent p-5" data-testid="btb-header-card">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative">
              <p className="text-[10px] uppercase tracking-[0.2em] text-blue-400/70 font-semibold">Bautagesbericht</p>
              <h2 className="text-xl font-bold text-white mt-1">{report.project_name}</h2>

              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 mt-4 text-xs">
                <div className={`flex items-center gap-1.5 ${fieldHasError("date") ? "text-red-400" : "text-gray-400"}`}>
                  <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{report.date ? format(new Date(report.date + "T12:00:00"), "dd. MMMM yyyy", { locale: de }) : "Datum fehlt"}</span>
                </div>
                <div className={`flex items-center gap-1.5 ${fieldHasError("project_address") ? "text-red-400" : "text-gray-400"}`}>
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="truncate">{displayAddress || "Adresse fehlt"}</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-400">
                  <Pen className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>{report.created_by_name}</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-400">
                  <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Nr. {reports.findIndex(r => r.id === report.id) + 1 || "–"}</span>
                </div>
              </div>

              {/* Validation + Badges */}
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {report.ai_generated && (
                  <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 bg-blue-500/12 text-blue-400 rounded-full font-medium border border-blue-500/20">
                    <Sparkles className="w-3 h-3" />KI generiert
                  </span>
                )}
                <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 bg-emerald-500/12 text-emerald-400 rounded-full font-medium border border-emerald-500/20">
                  <Shield className="w-3 h-3" />VOB/B SS3
                </span>
                {validation && (
                  <span className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium border ${validation.valid ? "bg-green-500/12 text-green-400 border-green-500/20" : "bg-red-500/12 text-red-400 border-red-500/20"}`}>
                    {validation.valid ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                    {validation.valid ? "Vollstaendig" : `${validation.errors.length} Fehler`}
                  </span>
                )}
                <button onClick={validateReport} disabled={validating} className="ml-auto text-[11px] text-blue-400 hover:text-blue-300 flex items-center gap-1 font-medium" data-testid="btb-validate-btn">
                  {validating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}Pruefen
                </button>
              </div>
            </div>
          </div>

          {/* Validation Errors */}
          {validation && !validation.valid && (
            <div className="rounded-xl border border-red-500/20 bg-red-500/8 p-3.5 space-y-1.5" data-testid="btb-validation-errors">
              <p className="text-xs font-semibold text-red-400 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" />Pflichtfelder fehlen</p>
              {validation.errors.map((e, i) => <p key={i} className="text-[11px] text-red-300 ml-5">- {e.message}</p>)}
              {validation.warnings.map((w, i) => <p key={`w-${i}`} className="text-[11px] text-amber-400 ml-5 flex items-center gap-1"><Info className="w-3 h-3" />{w.message}</p>)}
            </div>
          )}

          {/* ═══ 2. WETTER ═══ */}
          <Section icon={CloudSun} title="Wetter" testId="btb-weather-section">
            {editMode ? (
              <Input value={editWeather} onChange={e => setEditWeather(e.target.value)} placeholder="z.B. Sonnig, 18°C, leichter Wind"
                className="bg-white/8 border-white/10 text-white text-sm" data-testid="btb-weather-edit" />
            ) : (
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-300">{report.weather || "Nicht angegeben"}</p>
                {report.weather_auto && <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/12 text-blue-400 rounded-full border border-blue-500/20">Auto</span>}
              </div>
            )}
          </Section>

          {/* ═══ 3. ARBEITSZEITEN ═══ */}
          <Section icon={Clock} title="Arbeitszeiten" error={fieldHasError("work_time")} testId="btb-worktime-section">
            {editMode ? (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[10px] text-gray-500">Beginn</Label>
                    <Input type="time" value={editWorkStartManual || report.work_start || ""}
                      onChange={e => setEditWorkStartManual(e.target.value)}
                      className="bg-white/8 border-white/10 text-white text-sm h-10" data-testid="btb-workstart-edit" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-gray-500">Ende</Label>
                    <Input type="time" value={editWorkEndManual || report.work_end || ""}
                      onChange={e => setEditWorkEndManual(e.target.value)}
                      className="bg-white/8 border-white/10 text-white text-sm h-10" data-testid="btb-workend-edit" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-gray-500">Gesamt (h)</Label>
                    <Input type="number" step="0.5" value={editTotalHoursManual || report.total_work_hours || ""}
                      onChange={e => setEditTotalHoursManual(e.target.value)}
                      className="bg-white/8 border-white/10 text-white text-sm h-10" data-testid="btb-totalhours-edit" />
                  </div>
                </div>
                <p className="text-[10px] text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Manuelle Aenderungen werden als Ueberschreibung markiert</p>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-6">
                  {workStart && workEnd ? (
                    <>
                      <div className="text-center">
                        <p className="text-[10px] text-gray-500 mb-0.5">Beginn</p>
                        <p className="text-lg font-bold text-green-400 font-mono" data-testid="btb-work-start">{workStart}</p>
                      </div>
                      <span className="text-gray-600 text-lg">–</span>
                      <div className="text-center">
                        <p className="text-[10px] text-gray-500 mb-0.5">Ende</p>
                        <p className="text-lg font-bold text-red-400 font-mono" data-testid="btb-work-end">{workEnd}</p>
                      </div>
                      <div className="ml-auto text-right">
                        <p className="text-[10px] text-gray-500 mb-0.5">Netto</p>
                        <p className="text-lg font-bold text-white font-mono" data-testid="btb-total-hours">{totalHours}h</p>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                      <span className="text-sm text-amber-400">Arbeitszeit nicht erfasst</span>
                    </div>
                  )}
                </div>
                {hasManualOverride && (
                  <p className="text-[10px] text-amber-400 mt-2 flex items-center gap-1"><Edit3 className="w-3 h-3" />Manuell ueberschrieben</p>
                )}
              </div>
            )}
          </Section>

          {/* ═══ 4. PERSONAL / ANWESENHEIT ═══ */}
          <Section icon={Users} title={`Personal (${empDetails.length || (report.employees_on_site || []).length})`} error={fieldHasError("employees")} collapsible testId="btb-employees-section">
            {empDetails.length > 0 ? (
              <div className="space-y-1.5">
                {empDetails.map((e, i) => (
                  <div key={i} className="flex items-center justify-between bg-white/[0.04] border border-white/8 rounded-xl px-3 py-2.5" data-testid={`btb-emp-${i}`}>
                    <span className="text-sm text-gray-200 font-medium">{e.user_name}</span>
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      {e.work_start && e.work_end ? (
                        <>
                          <span className="text-green-400 font-mono">{e.work_start}</span>
                          <span className="text-gray-600">–</span>
                          <span className="text-red-400 font-mono">{e.work_end}</span>
                          <span className="text-gray-600">|</span>
                          <span className="text-white font-semibold">{e.net_work_time || "-"}</span>
                        </>
                      ) : (
                        <span className="text-amber-400 italic text-[11px]">Nicht erfasst</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (report.employees_on_site || []).length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {report.employees_on_site.map((e, i) => (
                  <span key={i} className="px-2.5 py-1 bg-white/8 rounded-lg text-xs text-gray-300 border border-white/8">{e.user_name}</span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-red-400">Keine Mitarbeiter erfasst</p>
            )}
          </Section>

          {/* ═══ 5. FREMDFIRMEN ═══ */}
          <Section icon={Building2} title={`Fremdfirmen (${subs.length})`} collapsible defaultOpen={subs.length > 0 || editMode} testId="btb-subcontractors-section">
            {editMode ? (
              <div className="space-y-2">
                {editSubcontractors.map((s, i) => (
                  <div key={i} className="bg-white/[0.04] border border-white/8 rounded-xl p-3 space-y-2" data-testid={`btb-sub-edit-${i}`}>
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-gray-500 font-medium">Fremdfirma {i + 1}</span>
                      <button onClick={() => removeSubcontractor(i)} className="p-1 text-red-400 hover:bg-red-500/10 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={s.company} onChange={e => updateSubcontractor(i, "company", e.target.value)} placeholder="Firmenname" className="bg-white/8 border-white/10 text-white text-xs h-9" />
                      <Input value={s.trade} onChange={e => updateSubcontractor(i, "trade", e.target.value)} placeholder="Gewerk" className="bg-white/8 border-white/10 text-white text-xs h-9" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" value={s.workers || ""} onChange={e => updateSubcontractor(i, "workers", parseInt(e.target.value) || 0)} placeholder="Personen" className="bg-white/8 border-white/10 text-white text-xs h-9" />
                      <Input value={s.notes} onChange={e => updateSubcontractor(i, "notes", e.target.value)} placeholder="Bemerkung" className="bg-white/8 border-white/10 text-white text-xs h-9" />
                    </div>
                  </div>
                ))}
                <button onClick={addSubcontractor} className="w-full py-2.5 border border-dashed border-white/15 rounded-xl text-xs text-gray-400 hover:text-white hover:border-white/30 flex items-center justify-center gap-1.5 transition-colors" data-testid="btb-add-sub">
                  <Plus className="w-3.5 h-3.5" />Fremdfirma hinzufuegen
                </button>
              </div>
            ) : subs.length > 0 ? (
              <div className="space-y-1.5">
                {subs.map((s, i) => (
                  <div key={i} className="flex items-center justify-between bg-white/[0.04] border border-white/8 rounded-xl px-3 py-2.5" data-testid={`btb-sub-${i}`}>
                    <div>
                      <span className="text-sm text-gray-200 font-medium">{s.company}</span>
                      {s.trade && <span className="text-xs text-gray-500 ml-2">{s.trade}</span>}
                    </div>
                    <span className="text-xs text-gray-400">{s.workers} Pers.</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-600">Keine Fremdfirmen erfasst</p>
            )}
          </Section>

          {/* ═══ 6. TAGESABLAUF / TIMELINE ═══ */}
          {timeline.length > 0 && (
            <Section icon={Timer} title={`Tagesablauf (${timeline.length})`} collapsible testId="btb-timeline-section">
              <div className="relative pl-4 border-l-2 border-blue-500/20 space-y-3">
                {timeline.map((ev, i) => (
                  <div key={i} className="relative" data-testid={`btb-timeline-${i}`}>
                    <div className="absolute -left-[calc(1rem+5px)] top-1.5 w-2.5 h-2.5 rounded-full bg-blue-500 border-2 border-[#0d0e14]" />
                    <div className="flex items-start gap-2.5">
                      <span className="text-xs font-mono text-blue-400 min-w-[3rem] pt-0.5">{ev.time}</span>
                      <div>
                        <p className="text-sm text-gray-200 font-medium">{ev.event}</p>
                        {ev.details && <p className="text-xs text-gray-500 mt-0.5">{ev.details}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ═══ 7. TAETIGKEITEN (KERNBEREICH) ═══ */}
          <div data-testid="btb-activities-section">
            <div className="flex items-center gap-2 mb-3">
              <FileText className={`w-4 h-4 ${fieldHasError("activities") ? "text-red-400" : "text-gray-500"}`} />
              <span className={`text-xs font-semibold tracking-wide uppercase ${fieldHasError("activities") ? "text-red-400" : "text-gray-400"}`}>
                Taetigkeiten ({(report.activities || []).length})
              </span>
            </div>
            {(report.activities || []).length === 0 && (
              <div className="rounded-xl border border-red-500/20 bg-red-500/8 p-4 text-center">
                <p className="text-xs text-red-400">Keine Taetigkeiten eingetragen (mind. 1 erforderlich)</p>
              </div>
            )}
            <div className="space-y-3">
              {(editMode ? editActivities : report.activities || []).map((act, idx) => (
                <ActivityCard
                  key={act.id || idx}
                  activity={act}
                  index={idx}
                  editMode={editMode}
                  onChange={handleActivityChange}
                  onPhotoClick={setOverlayPhoto}
                />
              ))}
            </div>
          </div>

          {/* ═══ 8. STOERUNGEN / BESONDERHEITEN (VOB Pflichtfeld) ═══ */}
          <Section icon={AlertTriangle} title="Stoerungen / Besonderheiten" error={false} testId="btb-disruptions-section">
            {editMode ? (
              <div className="space-y-2">
                <Textarea
                  value={editDisruptions}
                  onChange={e => setEditDisruptions(e.target.value)}
                  rows={4}
                  className="bg-white/8 border-white/10 text-white text-sm"
                  placeholder="Behinderungen, Verzoegerungen, Materialprobleme, Wetterprobleme...&#10;&#10;Standard: Keine Behinderungen festgestellt."
                  data-testid="btb-disruptions-edit"
                />
                <button
                  onClick={disruptionRecording ? stopDisruptionVoice : startDisruptionVoice}
                  disabled={disruptionTranscribing}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    disruptionRecording ? "bg-red-500 text-white animate-pulse" :
                    disruptionTranscribing ? "bg-gray-700 text-gray-400" :
                    "bg-white/8 text-gray-400 hover:bg-white/12 hover:text-white"
                  }`}
                  data-testid="btb-disruptions-voice"
                >
                  {disruptionTranscribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : disruptionRecording ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
                  {disruptionTranscribing ? "Wird erkannt..." : disruptionRecording ? "Stoppen" : "Spracheingabe"}
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">
                {disruptions || "Keine Behinderungen oder besonderen Vorkommnisse festgestellt."}
              </p>
            )}
          </Section>

          {/* ═══ 9. LEISTUNGSSTAND ═══ */}
          <Section icon={TrendingUp} title="Leistungsstand" testId="btb-progress-section">
            {editMode ? (
              <div className="flex items-center gap-3">
                <Input
                  type="number" min={0} max={100}
                  value={editProgress}
                  onChange={e => setEditProgress(e.target.value)}
                  className="bg-white/8 border-white/10 text-white text-sm w-24 h-10"
                  placeholder="%"
                  data-testid="btb-progress-edit"
                />
                <span className="text-sm text-gray-400">%</span>
                <div className="flex-1 h-3 bg-white/8 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min(parseInt(editProgress) || 0, 100)}%` }} />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold text-white" data-testid="btb-progress-value">{progressPct}%</span>
                <div className="flex-1 h-3 bg-white/8 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
                </div>
              </div>
            )}
          </Section>

          {/* ═══ 10. BEMERKUNGEN / VOB TEXTBLOCK ═══ */}
          <Section icon={Shield} title="Bemerkungen / VOB" testId="btb-notes-section">
            {editMode ? (
              <Textarea
                value={editNotes}
                onChange={e => setEditNotes(e.target.value)}
                rows={4}
                className="bg-white/8 border-white/10 text-white text-sm"
                placeholder="VOB-konforme Bemerkungen..."
                data-testid="btb-notes-edit"
              />
            ) : (
              <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">{report.notes || "Keine Bemerkungen"}</p>
            )}
          </Section>

          {/* ═══ 11. UNTERSCHRIFT ═══ */}
          <Section icon={Pen} title="Unterschrift / Bestaetigung" collapsible defaultOpen={false} testId="btb-signature-section">
            <div className="space-y-4">
              <div>
                <Label className="text-[10px] text-gray-500 mb-1 block">Mitarbeiter / Bauleiter</Label>
                <div className="h-20 bg-white/[0.04] border border-dashed border-white/15 rounded-xl flex items-center justify-center">
                  {report.signature_employee ? (
                    <p className="text-sm text-gray-300">{report.signature_employee}</p>
                  ) : (
                    <p className="text-xs text-gray-600">Noch nicht unterschrieben</p>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-gray-500 mb-1 block">Auftraggeber (optional)</Label>
                <div className="h-20 bg-white/[0.04] border border-dashed border-white/15 rounded-xl flex items-center justify-center">
                  {report.signature_customer ? (
                    <p className="text-sm text-gray-300">{report.signature_customer}</p>
                  ) : (
                    <p className="text-xs text-gray-600">Optional</p>
                  )}
                </div>
              </div>
            </div>
          </Section>

          {/* ═══ FOOTER ═══ */}
          <div className="border-t border-white/8 pt-4 space-y-1.5">
            <div className="flex items-center gap-2 text-[10px] text-gray-600">
              <Shield className="w-3 h-3" />
              <span>VOB/B SS3 konform | Bautagesbericht</span>
            </div>
            <p className="text-[10px] text-gray-600">
              Erstellt: {report.created_by_name} am {report.created_at ? format(new Date(report.created_at), "dd.MM.yyyy HH:mm", { locale: de }) : ""}
            </p>
            {report.updated_at !== report.created_at && (
              <p className="text-[10px] text-gray-600">
                Bearbeitet: {report.updated_at ? format(new Date(report.updated_at), "dd.MM.yyyy HH:mm", { locale: de }) : ""}
              </p>
            )}
            {(report.versions || []).length > 0 && (
              <button onClick={loadVersions} className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-1 mt-1" data-testid="btb-show-versions">
                <History className="w-3 h-3" />{report.versions.length} Aenderung(en)
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ━━━━━━━━━ LIST VIEW ━━━━━━━━━
  return (
    <div className="bg-[#0d0e14] text-white min-h-full pb-24" data-testid="btb-list-view">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0d0e14]/95 backdrop-blur-lg border-b border-white/8 px-4 py-3">
        <div className="flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors" data-testid="btb-back-list-btn">
            <ChevronLeft className="w-4 h-4" />Zurueck
          </button>
          <h2 className="text-sm font-semibold text-gray-200">Bautagesberichte</h2>
          <div className="w-8" />
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* ════════ PRIMARY ACTION: Neuer Bericht ════════ */}
        <button
          type="button"
          onClick={() => navigate(`/app/projekte/${projectId}/berichte/neu?date=${selectedDate}`)}
          className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl text-base font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all shadow-lg shadow-blue-600/20"
          data-testid="btb-new-report-btn"
        >
          <Plus className="w-5 h-5" /> Neuer Bautagesbericht
        </button>

        {/* ════════ LISTE (prominent oben) ════════ */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Berichte ({reports.length})</h3>
          </div>
          {loading ? (
            <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-600" /></div>
          ) : reports.length === 0 ? (
            <div className="text-center py-10 text-gray-600 bg-white/[0.02] rounded-2xl border border-white/5">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Noch keine Berichte</p>
              <p className="text-xs text-gray-700 mt-1">Tippe oben auf "Neuer Bautagesbericht"</p>
            </div>
          ) : (
            <div className="space-y-2">
              {reports.map(r => (
                <div key={r.id} className="bg-white/[0.04] border border-white/8 rounded-2xl p-4 transition-all"
                  data-testid={`btb-report-${r.id}`}
                >
                  <button
                    type="button"
                    onClick={() => openReport(r)}
                    className="w-full text-left"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-gray-100">
                            {r.date ? format(new Date(r.date + "T12:00:00"), "dd. MMMM yyyy", { locale: de }) : r.date}
                          </p>
                          {r.report_number && (
                            <span className="text-[10px] font-mono px-1.5 py-0.5 bg-blue-500/15 text-blue-300 rounded border border-blue-500/30">
                              {r.report_number}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><FileText className="w-3 h-3" /> {(r.activities || []).length}</span>
                          {(r.personnel || r.employees_on_site || []).length > 0 && (
                            <span className="flex items-center gap-1">
                              {(r.personnel || r.employees_on_site).length} MA
                            </span>
                          )}
                          {(r.photos || []).length > 0 && (
                            <span className="flex items-center gap-1 text-blue-400">
                              <Camera className="w-3 h-3" /> {(r.photos || []).length}
                            </span>
                          )}
                          {r.work_start && r.work_end && <span className="font-mono">{r.work_start}–{r.work_end}</span>}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {r.ai_generated && <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/12 text-blue-400 rounded-full border border-blue-500/20">KI</span>}
                        {r.total_work_hours > 0 && <p className="text-xs text-gray-300 font-mono font-semibold">{r.total_work_hours}h</p>}
                      </div>
                    </div>
                  </button>
                  {/* PDF Aktionen */}
                  <div className="flex gap-2 mt-3 pt-3 border-t border-white/8">
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          toast.loading("PDF wird erstellt...", { id: `pdf-${r.id}` });
                          const res = await authFetch(`${API}/daily-reports/${r.id}/pdf?inline=true`);
                          if (!res.ok) throw new Error();
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          window.open(url, "_blank");
                          toast.success("PDF geöffnet", { id: `pdf-${r.id}` });
                          setTimeout(() => URL.revokeObjectURL(url), 60000);
                        } catch {
                          toast.error("PDF-Vorschau fehlgeschlagen", { id: `pdf-${r.id}` });
                        }
                      }}
                      className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1 active:scale-[0.98] transition-all"
                      data-testid={`btb-mobile-pdf-preview-${r.id}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> PDF
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/app/projekte/${projectId}/berichte/${r.id}/bearbeiten`)}
                      className="flex-1 py-2.5 bg-white/8 hover:bg-white/12 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1 active:scale-[0.98] transition-all border border-white/10"
                      data-testid={`btb-mobile-edit-${r.id}`}
                    >
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          toast.loading("PDF wird erstellt...", { id: `pdf-dl-${r.id}` });
                          const res = await authFetch(`${API}/daily-reports/${r.id}/pdf`);
                          if (!res.ok) throw new Error();
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          const dateStr = r.date || "";
                          const projectName = (r.project_name || "Projekt").replace(/\s+/g, "_");
                          a.href = url;
                          a.download = `Bautagesbericht_${projectName}_${dateStr}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          setTimeout(() => URL.revokeObjectURL(url), 5000);
                          toast.success("Download gestartet", { id: `pdf-dl-${r.id}` });
                        } catch {
                          toast.error("Download fehlgeschlagen", { id: `pdf-dl-${r.id}` });
                        }
                      }}
                      className="px-3 py-2.5 bg-white/8 hover:bg-white/12 text-white rounded-lg text-xs font-semibold flex items-center justify-center gap-1 active:scale-[0.98] transition-all border border-white/10"
                      data-testid={`btb-mobile-pdf-download-${r.id}`}
                      title="Download PDF"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ════════ AI-GENERATOR (zusammenklappbar, sekundär) ════════ */}
        <details className="relative overflow-hidden rounded-2xl border border-blue-500/15 bg-gradient-to-br from-blue-600/8 via-indigo-600/5 to-transparent" data-testid="btb-generate-section">
          <summary className="cursor-pointer list-none p-4 flex items-center justify-between hover:bg-white/5 transition-colors">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-white">KI-Generator</span>
            </div>
            <span className="text-[10px] text-gray-500 uppercase tracking-wider">Auto aus Aufgaben</span>
          </summary>
          <div className="px-4 pb-4">
            <p className="text-xs text-gray-500 mb-4">
              Automatisch aus Aufgaben, Fotos, Stempelzeiten und Wetterdaten generiert.
            </p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div>
                <Label className="text-[10px] text-gray-500 mb-1 block">Datum</Label>
                <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
                  className="bg-white/8 border-white/10 text-white text-sm h-11" data-testid="btb-date-input" />
              </div>
              <div>
                <Label className="text-[10px] text-gray-500 mb-1 block">Wetter (optional)</Label>
                <Input value={editWeather} onChange={e => setEditWeather(e.target.value)} placeholder="Auto"
                  className="bg-white/8 border-white/10 text-white text-sm h-11" data-testid="btb-weather-input" />
              </div>
            </div>
            <button
              onClick={generateReport}
              disabled={generating}
              className="w-full py-3 bg-blue-600/80 hover:bg-blue-500 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[0.98] transition-all"
              data-testid="btb-generate-btn"
            >
              {generating ? <><Loader2 className="w-5 h-5 animate-spin" />Wird generiert...</> : <><Sparkles className="w-5 h-5" />Bericht generieren</>}
            </button>
          </div>
        </details>
      </div>
    </div>
  );
}
